import type { IApiBatter } from '@trackman/baseball-shared-components';
import { BallFinalKinds, HitTypes, constants, valueFormatters } from '@trackman/baseball-shared-components';
import type { IStrikeZoneDecisionModel } from 'interfaces/tagging/ApiModels/IStrikeZoneDecision';
import { action, computed, makeObservable, observable } from 'mobx';
import { custom, serializable } from 'serializr';
import { v1 as uuid } from 'uuid';
import { IApiBallFinal } from '../../interfaces/broadcast/IApiBallFinal';
import { Lineup } from './Lineup';

const simpleSerialize = (item: any) => JSON.stringify(item);
const simpleDeserialize = (json: any) => JSON.parse(json);

export class BpPlay {
  constructor(ballFinal?: IApiBallFinal) {
    this.id = uuid();
    this.playId = ballFinal ? ballFinal.playId : constants.emptyGuid;
    this.isChanged = false;
    this.isWarmup = false;
    this.isDeleted = false;
    this.batter = {} as IApiBatter;
    this.sequenceNumber = 0;
    this.playSequenceNumber = undefined;
    this.note = '';
    this.isNoteResolved = false;
    this.isPlaceholder = false;
    this.hitType = HitTypes.Undefined;
    this.playLineup = new Lineup([]);
    this.ballFinals = ballFinal ? [ballFinal] : [];
    this.strikeZoneDecision = undefined;
    this.trackStartTimeAutoGenerated = false;
    makeObservable(this);
  }

  @observable
  trackStartTimeAutoGenerated: boolean;

  @serializable
  @observable
  public id: string;

  @serializable
  @observable
  public playId?: string;

  @observable batter: IApiBatter;

  @serializable
  @observable
  isTagStored = false;

  @serializable
  @observable
  isValidated = false;

  @serializable
  @observable
  hitType: HitTypes;

  @serializable(custom(simpleSerialize, simpleDeserialize))
  @observable
  ballFinals: IApiBallFinal[];

  @observable
  isChanged: boolean;

  @serializable
  @observable
  isWarmup: boolean;

  @serializable
  @observable
  isDeleted: boolean;

  @serializable
  @observable
  sequenceNumber: number;

  @observable
  playSequenceNumber?: number;

  @observable
  note: string;

  @observable
  isNoteResolved: boolean;

  @observable
  isPlaceholder: boolean;

  @observable
  playLineup: Lineup;

  @observable
  shouldAnimate = false;

  @observable
  strikeZoneDecision?: IStrikeZoneDecisionModel;

  @action clearInningRelatedData = () => {
    this.batter = {} as IApiBatter;
  };

  @computed get isLive() {
    return !this.isWarmup && !this.isDeleted;
  }

  @computed get exitSpeed() {
    const bf = this.ballFinals.find((b) => b.kind === BallFinalKinds.Hit);
    if (!bf || !bf.start) {
      return undefined;
    }
    return valueFormatters.mphFormatter(bf.start.speed, 0);
  }

  @computed get trackStartTimeUtc(): string | undefined {
    const bf = this.ballFinals.find((b) => b.kind === BallFinalKinds.Pitch);
    return bf ? bf.trackStartTime : undefined;
  }
}
